/**
 * Seasonal decoration: a quiet fall of shapes, once a day.
 *
 * Deliberately not a splash screen. One canvas, fixed, taking no pointer
 * events, covering nothing that has to be read and dismissing itself. It
 * starts only once the page is idle and never runs at all for a visitor whose
 * system asks for reduced motion.
 *
 * Canvas rather than elements, after the first version was built out of 34
 * absolutely positioned spans. Each of those carried its own compositor layer
 * and two animations, which on an ordinary laptop is where the flicker came
 * from; here the whole effect is a single element and one paint per frame, so
 * it can afford more shapes, real depth and a proper ending.
 *
 * The ending matters. The first version simply deleted the layer at a fixed
 * moment, so the decoration did not finish — it vanished mid-air. This one
 * stops making new shapes, lets the ones in flight land, and fades the canvas
 * out, which is the difference between an effect and a glitch.
 *
 * Windows are read here rather than on the server because pages are cached
 * for a week: a date decided in PHP would be a week stale at both ends.
 */

const DAY = 86400000;
const RUN = 14000; // Shapes keep falling for this long…
const FADE = 2200; // …then the canvas fades out over this.
const DENSITY = 1 / 96000; // Shapes per pixel of viewport, so a phone gets fewer.
const MAX = 22;

/**
 * Today as MM-DD, in the reader's own timezone — the holiday is theirs.
 */
function today() {
	const now = new Date();
	const pad = ( n ) => String( n ).padStart( 2, '0' );

	return pad( now.getMonth() + 1 ) + '-' + pad( now.getDate() );
}

/**
 * Is MM-DD inside from..to, inclusive? A window whose end is before its start
 * wraps around New Year, which is exactly what the New Year one does.
 * @param {string} date Today, as MM-DD.
 * @param {string} from Window start, MM-DD.
 * @param {string} to   Window end, MM-DD, inclusive.
 */
function within( date, from, to ) {
	return from <= to ? date >= from && date <= to : date >= from || date <= to;
}

/**
 * The effect to run, or null.
 * @param {Object} cfg What the server handed over: mode, key and effects.
 */
function pick( cfg ) {
	if ( cfg.mode !== 'auto' ) {
		return cfg.effects[ cfg.mode ] ? cfg.mode : null;
	}

	const date = today();

	return (
		Object.keys( cfg.effects ).find( ( key ) =>
			within( date, cfg.effects[ key ].from, cfg.effects[ key ].to )
		) || null
	);
}

/* -------------------------------------------------------------------------
 * Shapes. Each draws itself centred on the origin inside a box of `s`, so the
 * caller only has to position, rotate and set the alpha.
 *
 * They are drawn, not fetched, and they have to survive being 20 px tall on a
 * cream background. That rules out detail and rules in silhouette: a pumpkin
 * is read from its ribs and its stalk, a bat from its wings, a leaf from its
 * point. The first version skipped all three and arrived as coloured blobs.
 * ---------------------------------------------------------------------- */

const TAU = Math.PI * 2;

/**
 * Lighten or darken a #rrggbb by a fraction, for ribs and shading.
 *
 * @param {string} hex   Colour.
 * @param {number} ratio -1…1, negative darkens.
 * @return {string} Colour.
 */
function shade( hex, ratio ) {
	const channel = ( at ) => parseInt( hex.slice( at, at + 2 ), 16 );
	const mix = ( c ) =>
		Math.max(
			0,
			Math.min(
				255,
				Math.round(
					ratio < 0 ? c * ( 1 + ratio ) : c + ( 255 - c ) * ratio
				)
			)
		);

	return (
		'rgb(' +
		mix( channel( 1 ) ) +
		',' +
		mix( channel( 3 ) ) +
		',' +
		mix( channel( 5 ) ) +
		')'
	);
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 */
function pumpkin( ctx, s, color ) {
	const r = s * 0.4;
	const y = s * 0.05;
	const body = () => {
		ctx.beginPath();
		ctx.ellipse( 0, y, r * 1.14, r * 0.94, 0, 0, TAU );
	};

	// The sphere. Light from the upper left, deep shade at the far edge: this
	// gradient does almost all of the work of saying "round".
	const lit = ctx.createRadialGradient(
		-r * 0.42,
		y - r * 0.46,
		r * 0.06,
		0,
		y,
		r * 1.3
	);

	lit.addColorStop( 0, shade( color, 0.44 ) );
	lit.addColorStop( 0.45, color );
	lit.addColorStop( 1, shade( color, -0.36 ) );

	ctx.fillStyle = lit;
	body();
	ctx.fill();

	/*
	 * Bounced light along the lower-right rim. A ball lit from one side only
	 * still reads as a disc; the light the table throws back at it is what
	 * turns the disc into a sphere.
	 */
	const rim = ctx.createRadialGradient(
		r * 0.6,
		y + r * 0.5,
		r * 0.04,
		r * 0.25,
		y + r * 0.25,
		r * 1.05
	);

	rim.addColorStop( 0, 'rgba(255,255,255,.3)' );
	rim.addColorStop( 1, 'rgba(255,255,255,0)' );
	ctx.fillStyle = rim;
	body();
	ctx.fill();

	// Ribs, narrowing towards the sides as they would wrap around a sphere.
	ctx.strokeStyle = 'rgba(43,33,28,.15)';
	ctx.lineWidth = Math.max( 1, s * 0.018 );
	[ 0.4, 0.74 ].forEach( ( f ) => {
		[ -1, 1 ].forEach( ( side ) => {
			ctx.beginPath();
			ctx.ellipse(
				side * r * f * 0.55,
				y,
				r * 0.22 * ( 1 - f * 0.45 ),
				r * 0.9 * ( 1 - f * 0.22 ),
				0,
				0,
				TAU
			);
			ctx.stroke();
		} );
	} );

	// Highlight, small and soft, where the light actually lands.
	ctx.fillStyle = 'rgba(255,255,255,.26)';
	ctx.beginPath();
	ctx.ellipse( -r * 0.44, y - r * 0.44, r * 0.22, r * 0.13, -0.5, 0, TAU );
	ctx.fill();

	// Stalk, lit from the same side so it belongs to the same object.
	ctx.strokeStyle = '#4A5733';
	ctx.lineWidth = Math.max( 1.6, s * 0.08 );
	ctx.lineCap = 'round';
	ctx.beginPath();
	ctx.moveTo( 0, y - r * 0.86 );
	ctx.quadraticCurveTo( s * 0.02, -s * 0.31, s * 0.1, -s * 0.36 );
	ctx.stroke();

	ctx.strokeStyle = 'rgba(255,255,255,.22)';
	ctx.lineWidth = Math.max( 1, s * 0.03 );
	ctx.beginPath();
	ctx.moveTo( -s * 0.01, y - r * 0.84 );
	ctx.quadraticCurveTo( s * 0.01, -s * 0.3, s * 0.07, -s * 0.345 );
	ctx.stroke();
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 */
function leaf( ctx, s, color ) {
	const w = s * 0.26;
	const h = s * 0.44;

	const light = ctx.createLinearGradient( -w, -h, w, h );
	light.addColorStop( 0, shade( color, 0.22 ) );
	light.addColorStop( 1, shade( color, -0.2 ) );

	ctx.fillStyle = light;
	ctx.beginPath();
	ctx.moveTo( 0, -h );
	ctx.quadraticCurveTo( w, -h * 0.15, 0, h );
	ctx.quadraticCurveTo( -w, -h * 0.15, 0, -h );
	ctx.fill();

	ctx.strokeStyle = shade( color, -0.3 );
	ctx.lineWidth = Math.max( 1, s * 0.035 );
	ctx.lineCap = 'round';
	ctx.beginPath();
	ctx.moveTo( 0, h * 0.86 );
	ctx.lineTo( 0, -h * 0.86 );
	ctx.stroke();

	// Two side veins; without them it is an olive, not a leaf.
	[ -1, 1 ].forEach( ( side ) => {
		ctx.beginPath();
		ctx.moveTo( 0, h * 0.12 );
		ctx.quadraticCurveTo(
			side * w * 0.4,
			-h * 0.1,
			side * w * 0.52,
			-h * 0.34
		);
		ctx.stroke();
	} );
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 */
function bat( ctx, s, color ) {
	ctx.fillStyle = color;

	// Body and ears.
	ctx.beginPath();
	ctx.ellipse( 0, 0, s * 0.1, s * 0.17, 0, 0, TAU );
	ctx.fill();

	[ -1, 1 ].forEach( ( side ) => {
		ctx.beginPath();
		ctx.moveTo( side * s * 0.03, -s * 0.13 );
		ctx.lineTo( side * s * 0.09, -s * 0.26 );
		ctx.lineTo( side * s * 0.1, -s * 0.1 );
		ctx.closePath();
		ctx.fill();

		// Wing: out, then back along a scalloped lower edge.
		ctx.beginPath();
		ctx.moveTo( side * s * 0.06, -s * 0.09 );
		ctx.quadraticCurveTo(
			side * s * 0.3,
			-s * 0.3,
			side * s * 0.5,
			-s * 0.12
		);
		ctx.quadraticCurveTo(
			side * s * 0.4,
			s * 0.02,
			side * s * 0.34,
			s * 0.14
		);
		ctx.quadraticCurveTo(
			side * s * 0.26,
			s * 0.02,
			side * s * 0.19,
			s * 0.15
		);
		ctx.quadraticCurveTo(
			side * s * 0.12,
			s * 0.04,
			side * s * 0.06,
			s * 0.1
		);
		ctx.closePath();
		ctx.fill();
	} );
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 * @param {boolean}                  arms  Draw the little branches.
 */
function snowflake( ctx, s, color, arms ) {
	ctx.strokeStyle = color;
	ctx.lineWidth = Math.max( 1, s * 0.06 );
	ctx.lineCap = 'round';

	for ( let i = 0; i < 3; i++ ) {
		const a = ( Math.PI / 3 ) * i;
		const dx = Math.cos( a ) * s * 0.45;
		const dy = Math.sin( a ) * s * 0.45;

		ctx.beginPath();
		ctx.moveTo( -dx, -dy );
		ctx.lineTo( dx, dy );
		ctx.stroke();

		if ( ! arms ) {
			continue;
		}

		[ -1, 1 ].forEach( ( end ) => {
			const bx = dx * end * 0.58;
			const by = dy * end * 0.58;

			[ -0.6, 0.6 ].forEach( ( turn ) => {
				ctx.beginPath();
				ctx.moveTo( bx, by );
				ctx.lineTo(
					bx + Math.cos( a + turn ) * s * 0.17 * end,
					by + Math.sin( a + turn ) * s * 0.17 * end
				);
				ctx.stroke();
			} );
		} );
	}
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 */
function heart( ctx, s, color ) {
	const w = s * 0.42;

	ctx.fillStyle = color;
	ctx.beginPath();
	ctx.moveTo( 0, w * 0.95 );
	ctx.bezierCurveTo( -w * 1.5, -w * 0.2, -w * 0.55, -w * 1.25, 0, -w * 0.45 );
	ctx.bezierCurveTo( w * 0.55, -w * 1.25, w * 1.5, -w * 0.2, 0, w * 0.95 );
	ctx.fill();
}

/**
 * @param {CanvasRenderingContext2D} ctx   Canvas.
 * @param {number}                   s     Box size.
 * @param {string}                   color Colour.
 */
function petal( ctx, s, color ) {
	ctx.fillStyle = color;
	ctx.beginPath();
	ctx.ellipse( 0, 0, s * 0.19, s * 0.42, 0, 0, TAU );
	ctx.fill();
}

const SHAPES = {
	pumpkin,
	leaf,
	bat,
	heart,
	petal,
	snowflake: ( ctx, s, c ) => snowflake( ctx, s, c, true ),
	crystal: ( ctx, s, c ) => snowflake( ctx, s, c, false ),
};

/**
 * Run it.
 */
export default function initSeasonal() {
	const cfg = window.winonwebSeasonal;

	if ( ! cfg || ! cfg.effects || ! window.requestAnimationFrame ) {
		return;
	}

	if (
		window.matchMedia &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	) {
		return;
	}

	const key = pick( cfg );

	if ( ! key ) {
		return;
	}

	/*
	 * ?seasonal on the URL shows it regardless of the once-a-day rule, which
	 * is the only sane way to look at a December effect in June — and a query
	 * string also walks past the page cache, so what you see is this build.
	 */
	const preview = window.location.search.indexOf( 'seasonal' ) !== -1;

	// Once a day per visitor. A browser that refuses storage simply sees it
	// every time, which is a smaller problem than not running at all.
	if ( ! preview ) {
		try {
			const seen = Number( window.localStorage.getItem( cfg.key ) );

			if ( seen && Date.now() - seen < DAY ) {
				return;
			}

			// Written before the first frame, so a reload mid-fall does not
			// start the whole thing again.
			window.localStorage.setItem( cfg.key, String( Date.now() ) );
		} catch ( e ) {} // eslint-disable-line no-empty
	}

	const effect = cfg.effects[ key ];

	/*
	 * The mix, expanded by weight into a pool to draw from. Five pumpkins to
	 * three leaves to two bats is a Halloween sky; one of each in rotation is
	 * a pattern, and the eye picks a pattern out instantly.
	 */
	const pool = [];

	( effect.bits || [] ).forEach( ( bit ) => {
		if ( ! bit.images && ! SHAPES[ bit.shape ] ) {
			return;
		}

		for ( let i = 0; i < Math.max( 1, bit.weight || 1 ); i++ ) {
			pool.push( bit );
		}
	} );

	if ( ! pool.length ) {
		return;
	}

	const rand = ( min, max ) => min + Math.random() * ( max - min );

	/**
	 * Every picture the mix needs, loaded before the first frame.
	 *
	 * Decoding a WebP mid-animation would show as a stutter, and a sprite
	 * built from a half-loaded image is an empty square. One that fails to
	 * load drops out of the mix instead of taking the effect down with it.
	 *
	 * @return {Promise} Resolves to a map of url => HTMLImageElement.
	 */
	const artwork = () => {
		const urls = [];

		pool.forEach( ( bit ) =>
			( bit.images || [] ).forEach( ( url ) => {
				if ( urls.indexOf( url ) === -1 ) {
					urls.push( url );
				}
			} )
		);

		return Promise.all(
			urls.map(
				( url ) =>
					new Promise( ( done ) => {
						const img = new window.Image();

						img.onload = () => done( [ url, img ] );
						img.onerror = () => done( null );
						img.src = url;
					} )
			)
		).then( ( pairs ) => {
			const loaded = {};

			pairs.filter( Boolean ).forEach( ( [ url, img ] ) => {
				loaded[ url ] = img;
			} );

			return loaded;
		} );
	};

	const start = ( art ) => {
		const canvas = document.createElement( 'canvas' );
		canvas.className = 'seasonal';
		canvas.setAttribute( 'aria-hidden', 'true' );
		document.body.appendChild( canvas );

		const ctx = canvas.getContext( '2d' );
		let w = 0;
		let h = 0;
		let dpr = 1;

		const resize = () => {
			dpr = Math.min( window.devicePixelRatio || 1, 2 );
			w = window.innerWidth;
			h = window.innerHeight;
			canvas.width = Math.round( w * dpr );
			canvas.height = Math.round( h * dpr );
			ctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
		};

		resize();
		window.addEventListener( 'resize', resize, { passive: true } );

		const count = Math.min(
			MAX,
			Math.max( 9, Math.round( w * h * DENSITY ) )
		);
		const bits = [];

		/*
		 * Each shape is drawn once, into its own small canvas, with its shadow
		 * and its blur already in the pixels. Every frame after that is a
		 * drawImage, which is what makes soft edges affordable: asking the
		 * canvas to blur twenty shapes sixty times a second is not.
		 */
		const sprite = ( paint, size, blur ) => {
			const pad = size * 0.4 + blur * 2.5;
			const box = Math.ceil( size + pad * 2 );
			const off = document.createElement( 'canvas' );

			off.width = Math.ceil( box * dpr );
			off.height = Math.ceil( box * dpr );

			const sctx = off.getContext( '2d' );

			sctx.setTransform( dpr, 0, 0, dpr, 0, 0 );
			sctx.translate( box / 2, box / 2 );
			sctx.shadowColor = 'rgba(43,33,28,.22)';
			sctx.shadowBlur = size * 0.16;
			sctx.shadowOffsetY = size * 0.05;

			if ( blur > 0 ) {
				sctx.filter = 'blur(' + blur.toFixed( 2 ) + 'px)';
			}

			paint( sctx, size );

			return { canvas: off, box };
		};

		for ( let i = 0; i < count; i++ ) {
			/*
			 * One number decides size, speed and opacity together. That is the
			 * whole trick behind depth: far things are small, slow and faint,
			 * near things large, quick and solid, and the eye reads a space
			 * instead of a flat sheet of confetti.
			 */
			const depth = Math.random();
			const bit = pool[ Math.floor( Math.random() * pool.length ) ];
			const size = Math.round( 34 + depth * 76 );
			const pictures = ( bit.images || [] )
				.map( ( url ) => art[ url ] )
				.filter( Boolean );
			const blur = ( 1 - depth ) * 2.6; // Far things are out of focus.
			let paint;

			if ( pictures.length ) {
				const img =
					pictures[ Math.floor( Math.random() * pictures.length ) ];
				// Keep the photograph's own proportions; a squashed pumpkin
				// is the one thing worse than a drawn one.
				const scale = size / Math.max( img.width, img.height );
				const iw = img.width * scale;
				const ih = img.height * scale;

				paint = ( c ) => c.drawImage( img, -iw / 2, -ih / 2, iw, ih );
			} else {
				const colour =
					bit.colors[
						Math.floor( Math.random() * bit.colors.length )
					];

				paint = ( c, sz ) => SHAPES[ bit.shape ]( c, sz, colour );
			}

			const sprite_ = sprite( paint, size, blur );

			bits.push( {
				depth,
				x: rand( -0.05, 1.05 ) * w,
				/*
				 * Most of them start on screen. Filling the sky above the
				 * window instead looks tidy in the code and empty in the
				 * browser: at these speeds a shape a screen-height up needs
				 * twenty seconds to arrive, and by then the effect is over.
				 */
				y: rand( -0.4, 1 ) * h,
				size,
				art: sprite_,
				fall: 44 + depth * 86, // px per second
				alpha: 0.3 + depth * 0.45,
				phase: rand( 0, Math.PI * 2 ),
				swing: rand( 0.2, 0.5 ), // radians per second
				drift: rand( 14, 40 ) * ( 0.5 + depth ),
				turn: bit.solid ? rand( -0.22, 0.22 ) : rand( -0.55, 0.55 ),
				angle: rand( 0, Math.PI * 2 ),
				/*
				 * The second axis, for flat things only. A leaf going edge-on
				 * is what a leaf does; a pumpkin doing it is a coin, and the
				 * moment the eye sees a ball flatten, the roundness it was
				 * shaded so carefully for is gone.
				 */
				solid: !! bit.solid,
				tumble: rand( 0, Math.PI * 2 ),
				flip: bit.solid ? 0 : rand( 0.5, 1.3 ),
			} );
		}

		const began = performance.now();
		let last = began;
		let raf = 0;

		const stop = () => {
			window.cancelAnimationFrame( raf );
			window.removeEventListener( 'resize', resize );
			document.removeEventListener( 'visibilitychange', onVisibility );
			canvas.remove();
		};

		// A hidden tab should not be animating anything; picking the clock up
		// again on return also stops everything teleporting after a minute away.
		function onVisibility() {
			if ( ! document.hidden ) {
				last = performance.now();
			}
		}

		document.addEventListener( 'visibilitychange', onVisibility );

		const frame = ( now ) => {
			raf = window.requestAnimationFrame( frame );

			if ( document.hidden ) {
				return;
			}

			const age = now - began;
			const fading = Math.max( 0, age - RUN );

			if ( fading >= FADE ) {
				stop();
				return;
			}

			// Capped, so a stalled tab does not teleport everything on return.
			const dt = Math.min( ( now - last ) / 1000, 0.05 );

			last = now;

			ctx.clearRect( 0, 0, w, h );
			ctx.globalAlpha = 1;

			const curtain = 1 - fading / FADE;

			bits.forEach( ( b ) => {
				b.y += b.fall * dt;
				b.phase += b.swing * dt;
				b.angle += b.turn * dt;

				// Past the bottom: fold it back up, but only while the effect
				// is still running, so the ending empties the screen.
				if ( b.y - b.size > h ) {
					if ( age < RUN ) {
						b.y = -b.size * 2;
						b.x = rand( -0.05, 1.05 ) * w;
					}

					return;
				}

				b.tumble += b.flip * dt;

				ctx.save();
				ctx.translate( b.x + Math.sin( b.phase ) * b.drift, b.y );
				ctx.rotate( b.angle );

				if ( ! b.solid ) {
					ctx.scale(
						Math.max( 0.18, Math.abs( Math.cos( b.tumble ) ) ),
						1
					);
				}
				ctx.globalAlpha = b.alpha * curtain;
				ctx.drawImage(
					b.art.canvas,
					-b.art.box / 2,
					-b.art.box / 2,
					b.art.box,
					b.art.box
				);
				ctx.restore();
			} );
		};

		raf = window.requestAnimationFrame( frame );
	};

	// After the page has settled, so nothing here competes with the article.
	const begin = () => artwork().then( start );

	if ( window.requestIdleCallback ) {
		window.requestIdleCallback( begin, { timeout: 1200 } );
	} else {
		window.setTimeout( begin, 600 );
	}
}
